/**
 * @file useCreateFeaturedDeal.js
 */

import { useMutation } from "@apollo/client";
import { CREATE_FEATURED_DEAL } from "cache/FeaturedDeal/mutations"; // Update the path as per your project structure

/**
 * @hook useCreateFeaturedDeal
 *
 * @description
 * Custom hook that handles the functionality to create a featured deal.
 * This hook can be used wherever the application needs to create a new featured deal.
 *
 * @returns
 * - createFeaturedDeal: Function to execute the create featured deal mutation
 * - loading: Boolean indicating the loading state
 * - error: Error object if there's an error
 */
const useCreateFeaturedDeal = () => {

	const [createFeaturedDealMutation, { loading, error }] = useMutation(CREATE_FEATURED_DEAL);

	/**
	 * Custom mutation to create a featured deal.
	 *
	 * @param {Object} input - The input object for creating a featured deal.
	 * @property {String} input.name - The name of the featured deal.
	 * @property {String} input.description - The description of the featured deal.
	 * @property {Array} input.images - The images of the featured deal.
	 * @property {String} input.expires - The expiration date of the featured deal.
	 * @returns {Object} - An object containing the created featured deal data or an error object.
	 * @throws {Error} - Throws an error if the mutation execution fails.
	 */
	const createFeaturedDeal = async (input) => {
		try {
			const { data } = await createFeaturedDealMutation({
				variables: {
					input // Pass the entire input object as a variable
				}
			});
			if (error) {
				throw new Error(error);
			}
			// Return the created featured deal data
			return { data: data.createFeaturedDeal };
		} catch (error) {
			return { error };
		}
	};

	return { createFeaturedDeal, loading, error };
};

export default useCreateFeaturedDeal;
